import {
  type PayloadAction,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';

// ========================================
// Search Results Caching Infrastructure
// ========================================
/**
 * In-memory cache for search results to prevent redundant API calls.
 * Key: hash of (query + filters + sort params + pagination)
 * Value: { data, timestamp, query }
 *
 * Cache is automatically cleared on page refresh/navigation.
 * TTL: 3 minutes (legal data changes infrequently)
 */
interface SearchCacheEntry {
  data: SearchPageResultsData;
  timestamp: number;
  query: string; // For debugging
}

const searchResultsCache = new Map<string, SearchCacheEntry>();
const SEARCH_CACHE_TTL = 3 * 60 * 1000; // 3 minutes
const MAX_SEARCH_CACHE_ENTRIES = 50;

// Forward declaration - interface defined below
export interface SearchCacheKeyParams {
  query: string;
  filters: Record<string, unknown> | null;
  sortType: SortType;
  legislationDateField: LegislationDateField;
  sortDirection: SortDirection;
  page: number;
  pageSize: number;
}

/**
 * Simple djb2 hash for cache key generation
 */
function hashString(str: string): string {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) + hash + str.charCodeAt(i);
  }
  return Math.abs(hash).toString(36);
}

/**
 * Normalize filters for deterministic cache key generation.
 * Sorts arrays by ID to ensure same filters produce same key.
 */
function normalizeFiltersForCache(
  filters: Record<string, unknown> | null
): string {
  if (!filters) return '';

  const sortArrayById = (arr: unknown[]): unknown[] => {
    if (!Array.isArray(arr)) return arr;
    return [...arr].sort((a, b) => {
      const aId = (a as { id?: string | number })?.id ?? '';
      const bId = (b as { id?: string | number })?.id ?? '';
      return String(aId).localeCompare(String(bId));
    });
  };

  const normalizeObject = (
    obj: Record<string, unknown>
  ): Record<string, unknown> => {
    const result: Record<string, unknown> = {};
    const sortedKeys = Object.keys(obj).sort();

    for (const key of sortedKeys) {
      const value = obj[key];
      if (Array.isArray(value)) {
        result[key] = sortArrayById(value);
      } else if (value && typeof value === 'object') {
        result[key] = normalizeObject(value as Record<string, unknown>);
      } else {
        result[key] = value;
      }
    }
    return result;
  };

  return JSON.stringify(normalizeObject(filters));
}

/**
 * Generate deterministic cache key from search parameters
 */
export function generateSearchCacheKey(params: SearchCacheKeyParams): string {
  const keyParts = [
    params.query.trim().toLowerCase(),
    normalizeFiltersForCache(params.filters),
    params.sortType,
    params.legislationDateField,
    params.sortDirection,
    params.page.toString(),
    params.pageSize.toString(),
  ];

  return hashString(keyParts.join('|'));
}

/**
 * Get cached search results if valid (not expired)
 */
export function getCachedSearchResults(
  cacheKey: string
): SearchPageResultsData | null {
  const cached = searchResultsCache.get(cacheKey);
  const now = Date.now();

  if (cached && now - cached.timestamp < SEARCH_CACHE_TTL) {
    return cached.data;
  }

  if (cached) {
    // Expired entry - remove it
    searchResultsCache.delete(cacheKey);
  }

  return null;
}

/**
 * Store search results in cache with LRU-style eviction
 */
export function cacheSearchResults(
  cacheKey: string,
  data: SearchPageResultsData,
  query: string
): void {
  // Enforce max cache size (LRU-style eviction)
  if (searchResultsCache.size >= MAX_SEARCH_CACHE_ENTRIES) {
    let oldestKey = '';
    let oldestTime = Date.now();

    searchResultsCache.forEach((entry, key) => {
      if (entry.timestamp < oldestTime) {
        oldestTime = entry.timestamp;
        oldestKey = key;
      }
    });

    if (oldestKey) {
      searchResultsCache.delete(oldestKey);
    }
  }

  searchResultsCache.set(cacheKey, {
    data,
    timestamp: Date.now(),
    query,
  });
}

/**
 * Invalidate all search cache entries.
 * Call when filters change.
 */
export function invalidateSearchCache(): void {
  if (searchResultsCache.size > 0) {
    searchResultsCache.clear();
  }
}

// ========================================
// End Caching Infrastructure
// ========================================

// Search result item from backend (matches SearchResultDocument from state.py)
export interface SearchResultItem {
  // Core document identification
  document_content_id: number;
  doc_type: 'legislation' | 'decision';

  // Search ranking scores
  bin_dense_hamming_dist?: number; // Dense search: Hamming distance (lower = better)
  fulltext_score?: number; // Fulltext search: PGroonga score (higher = better)
  pgroonga_score?: number; // Alias for fulltext_score
  rerank_score?: number; // Cross-encoder reranking score (higher = better)
  relevance_score?: number; // Final relevance score after reranking

  // Legislation-specific fields
  iri?: string; // Legislation IRI (e.g., '40/1964')
  jurisdiction?: string; // Legislative jurisdiction ('SK', 'EU', 'CZ', 'CSFR')
  effective_from?: string; // Legislation effective date (ISO format)
  declared?: string; // Legislation publication date from DB column 'declared' (ISO format)
  publication_date?: string; // Alias for declared (for backwards compatibility)
  legislation_areas?: string[]; // Právne oblasti for legislation (e.g., ["Občianske právo"])

  // Decision-specific fields
  type_of_decisions?: string; // Type of decision for decisions
  release_date?: string; // Decision release/decision date (ISO format) - from backend
  case_file_number?: string; // Spisová značka (e.g., "3Obdo/27/2018") - used as title for decisions
  areas?: string[]; // Právne oblasti for decisions
  courts?: string[]; // Súdy (courts) for decisions
  judges?: string[]; // Sudcovia (judges) for decisions

  // Document storage paths
  bucket_path?: string; // Supabase storage bucket path
  file_original_path?: string; // Original file path
  document_file_type?: 'HTML' | 'PDF'; // File type
  document_file_name?: string; // Filename

  // Document content
  document_text?: string; // Document summary or content

  // Search source
  search_source?: 'fulltext' | 'dense';

  // Match type indicator (from reranking)
  is_significant_match?: boolean; // True = exact field match, False = semantic match

  // Original source URL for external links (slov-lex.sk)
  original_source?: string;

  // Legislation metadata fields
  legislation_type_name?: string; // Legislation type display name (e.g., "Zákon", "Nariadenie vlády")
  law_type_name?: string; // Backend alias for legislation_type_name (from SQL law_type.type column)
  regulation_number?: string; // Regulation number for deduplication
  effective_till?: string; // Legislation effective until date (ISO format)

  // Full-text content search
  fulltext_snippet?: string; // Highlighted excerpt from full_text match (HTML with <span class="keyword">)

  // Section rows (legislation §): the act's id is shared by all its sections
  section_id?: string | null; // e.g. "paragraf-212"; absent/null on document-level rows
}

// SSE event data for search_page_results
export interface SearchPageResultsData {
  results: SearchResultItem[];
  total: number;
  page: number;
  page_size: number;
  has_more: boolean;
}

// Sort type: relevancy (score-based) or date (chronological)
export type SortType = 'relevancy' | 'date';

// Date field options for legislation
export type LegislationDateField = 'effective_from' | 'publication_date';

// Date field options for decisions
export type DecisionDateField = 'decision_date';

// Legacy sort field types (kept for compatibility)
type LegislationSortField = 'publication_date' | 'effective_from' | 'title';
type DecisionSortField = 'decision_date' | 'case_number' | 'court';
type SortField = LegislationSortField | DecisionSortField | 'score';
export type SortDirection = 'asc' | 'desc';
export type ViewMode = 'cards' | 'table';

interface SearchState {
  // Query state
  query: string;
  // Search page results
  sessionId: string | null;
  results: SearchResultItem[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
  // Sorting - always available (relevancy or date)
  sortType: SortType;
  legislationDateField: LegislationDateField; // Which date field to use for legislation (effective_from or publication_date)
  decisionDateField: DecisionDateField; // Which date field to use for decisions (currently only decision_date)
  sortDirection: SortDirection;
  // Legacy field (kept for compatibility with backend)
  sortField: SortField | null;
  // View mode
  viewMode: ViewMode;
  // Research session created from search results (for "Continue in Chat" navigation)
  researchSessionId: string | null;
  // Loading states
  isLoading: boolean;
  isStreaming: boolean;
  error: string | null;
}

const initialState: SearchState = {
  query: '',
  sessionId: null,
  results: [],
  total: 0,
  page: 1,
  pageSize: 10,
  hasMore: false,
  sortType: 'relevancy',
  legislationDateField: 'effective_from',
  decisionDateField: 'decision_date',
  sortDirection: 'desc',
  sortField: null,
  viewMode: 'cards',
  researchSessionId: null,
  isLoading: false,
  isStreaming: false,
  error: null,
};

const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.query = action.payload;
    },
    clearSearchQuery: (state) => {
      state.query = '';
    },
    // Search page flow actions
    startSearchPageFlow: (
      state,
      action: PayloadAction<{ sessionId: string; query: string }>
    ) => {
      state.sessionId = action.payload.sessionId;
      state.query = action.payload.query;
      state.isLoading = true;
      state.isStreaming = true;
      state.error = null;
      state.results = [];
      state.total = 0;
      state.hasMore = false;
      state.researchSessionId = null; // Reset on new search
    },
    startSearchPagePagination: (state) => {
      // For pagination - just set loading state without clearing results
      state.isLoading = true;
      state.isStreaming = true;
      state.error = null;
    },
    setSearchPageResults: (
      state,
      action: PayloadAction<SearchPageResultsData>
    ) => {
      state.results = action.payload.results;
      state.total = action.payload.total;
      state.page = action.payload.page;
      state.pageSize = action.payload.page_size;
      state.hasMore = action.payload.has_more;
      state.isLoading = false;
      state.isStreaming = false; // Results received = streaming complete (mobile SSE reliability fix)
    },
    appendSearchPageResults: (
      state,
      action: PayloadAction<SearchPageResultsData>
    ) => {
      // For pagination - append new results
      state.results = [...state.results, ...action.payload.results];
      state.total = action.payload.total;
      state.page = action.payload.page;
      state.pageSize = action.payload.page_size;
      state.hasMore = action.payload.has_more;
      state.isLoading = false;
      state.isStreaming = false; // Results received = streaming complete (mobile SSE reliability fix)
    },
    setSearchPageError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isLoading = false;
      state.isStreaming = false;
    },
    finishSearchPageStream: (state) => {
      state.isStreaming = false;
      state.isLoading = false;
    },
    clearSearchPageResults: (state) => {
      state.sessionId = null;
      state.researchSessionId = null;
      state.results = [];
      state.total = 0;
      state.page = 1;
      state.hasMore = false;
      state.isLoading = false;
      state.isStreaming = false;
      state.error = null;
    },
    // Sorting actions
    setSortType: (state, action: PayloadAction<SortType>) => {
      state.sortType = action.payload;
      // Update legacy sortField for backend compatibility
      if (action.payload === 'relevancy') {
        state.sortField = null;
      } else {
        // When switching to date, use the current legislationDateField
        state.sortField = state.legislationDateField;
      }
    },
    setLegislationDateField: (
      state,
      action: PayloadAction<LegislationDateField>
    ) => {
      state.legislationDateField = action.payload;
      // Update legacy sortField if currently sorting by date
      if (state.sortType === 'date') {
        state.sortField = action.payload;
      }
    },
    setDecisionDateField: (state, action: PayloadAction<DecisionDateField>) => {
      state.decisionDateField = action.payload;
    },
    setSortField: (state, action: PayloadAction<SortField | null>) => {
      state.sortField = action.payload;
    },
    setSortDirection: (state, action: PayloadAction<SortDirection>) => {
      state.sortDirection = action.payload;
    },
    toggleSortDirection: (state) => {
      state.sortDirection = state.sortDirection === 'asc' ? 'desc' : 'asc';
    },
    // View mode actions
    setViewMode: (state, action: PayloadAction<ViewMode>) => {
      state.viewMode = action.payload;
    },
    // Research session created from search (for "Continue in Chat" navigation)
    setResearchSessionId: (state, action: PayloadAction<string | null>) => {
      state.researchSessionId = action.payload;
    },
    // Page navigation
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
  },
});

export const {
  setSearchQuery,
  startSearchPageFlow,
  startSearchPagePagination,
  setSearchPageResults,
  appendSearchPageResults,
  setSearchPageError,
  finishSearchPageStream,
  setResearchSessionId,

  setSortType,
  setLegislationDateField,
  setDecisionDateField,

  toggleSortDirection,
  setViewMode,
} = searchSlice.actions;
export default searchSlice.reducer;

// Selectors
const selectResults = (state: { search: SearchState }) => state.search.results;
const selectSortDirection = (state: { search: SearchState }) =>
  state.search.sortDirection;
const selectSortType = (state: { search: SearchState }) =>
  state.search.sortType;

// Helper to get date value for sorting (handles both legislation and decision date fields)
const getDateValue = (
  item: SearchResultItem,
  legislationDateField: LegislationDateField
): number => {
  let dateStr: string | undefined;

  if (item.doc_type === 'legislation') {
    dateStr =
      legislationDateField === 'effective_from'
        ? item.effective_from
        : item.declared || item.publication_date; // declared is the DB column name
  } else {
    // Decision - use release_date
    dateStr = item.release_date;
  }

  if (!dateStr) return 0;

  try {
    return new Date(dateStr).getTime();
  } catch {
    return 0;
  }
};

const selectLegislationDateField = (state: { search: SearchState }) =>
  state.search.legislationDateField;

/**
 * Memoized selector that returns results sorted by current sort type and direction.
 * Supports sorting by:
 * - relevancy: uses relevance_score (higher = more relevant) - client-side sorting
 * - date: sorts by date field (effective_from/publication_date for legislation, release_date for decisions)
 *
 * Note: Always returns a new array to ensure React re-renders when sort changes.
 */
export const selectSortedResults = createSelector(
  [
    selectResults,
    selectSortType,
    selectSortDirection,
    selectLegislationDateField,
  ],
  (results, sortType, sortDirection, legislationDateField) => {
    if (results.length === 0) return [];

    // Create a new array (spread creates shallow copy)
    const sortedResults = [...results];

    if (sortType === 'date') {
      // Client-side date sorting
      sortedResults.sort((a, b) => {
        const dateA = getDateValue(a, legislationDateField);
        const dateB = getDateValue(b, legislationDateField);

        const diff = dateA - dateB;
        return sortDirection === 'desc' ? -diff : diff;
      });
    } else {
      // Client-side relevancy sorting
      sortedResults.sort((a, b) => {
        const scoreA = a.relevance_score ?? 0;
        const scoreB = b.relevance_score ?? 0;

        const diff = scoreA - scoreB;
        return sortDirection === 'desc' ? -diff : diff;
      });
    }

    return sortedResults;
  }
);
